/* Personal Book — the client's private space for progress pictures, a journal,
   and a compiled downloadable / shareable book. Lives inside the Client Dashboard. */
import { useEffect, useState } from "react";
import api, { formatApiError } from "../api/client";
import FileUploader from "./FileUploader";
import {
  BookOpen, Camera, NotebookPen, Download, Share2, Trash2, Copy, Check, Link as LinkIcon,
  Plus,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function PersonalBook() {
  const [pictures, setPictures] = useState([]);
  const [journal, setJournal] = useState([]);
  const [share, setShare] = useState({ enabled: false });
  const [jTitle, setJTitle] = useState("");
  const [jContent, setJContent] = useState("");
  const [jMood, setJMood] = useState("");
  const [jBusy, setJBusy] = useState(false);
  const [picBusy, setPicBusy] = useState(false);
  const [picCaption, setPicCaption] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try {
      const [p, j, s] = await Promise.all([
        api.get("/pictures"),
        api.get("/journal"),
        api.get("/personal-book/share/status"),
      ]);
      setPictures(p.data || []);
      setJournal(j.data || []);
      setShare(s.data || { enabled: false });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { refresh(); }, []);

  const addEntry = async (e) => {
    e.preventDefault();
    setJBusy(true); setErr("");
    try {
      await api.post("/journal", {
        title: jTitle.trim() || null,
        content: jContent.trim(),
        mood: jMood.trim() || null,
      });
      setJTitle(""); setJContent(""); setJMood("");
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setJBusy(false); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/journal/${id}`);
    refresh();
  };

  const attachPicture = async (file) => {
    setPicBusy(true); setErr("");
    try {
      await api.post("/pictures", {
        file_id: file.id,
        caption: picCaption.trim() || null,
      });
      setPicCaption("");
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPicBusy(false); }
  };

  const removePicture = async (id) => {
    if (!window.confirm("Delete this picture?")) return;
    await api.delete(`/pictures/${id}`);
    refresh();
  };

  const downloadPdf = async () => {
    // Use the authenticated axios client to fetch the PDF as a blob
    const res = await api.get("/personal-book/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Personal_Book.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const createShare = async () => {
    const { data } = await api.post("/personal-book/share");
    setShare({ enabled: true, ...data });
  };
  const revokeShare = async () => {
    if (!window.confirm("Revoke the share link? Anyone with the old link will lose access.")) return;
    await api.delete("/personal-book/share");
    setShare({ enabled: false });
  };
  const copyShare = async () => {
    if (!share.share_url) return;
    try {
      await navigator.clipboard.writeText(share.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  const token = localStorage.getItem("youfit_token");
  const pictureSrc = (pic) => `${API_BASE}/api/files/${pic.file_id}?auth=${encodeURIComponent(token || "")}`;

  return (
    <div className="space-y-10" data-testid="personal-book">
      {/* Header + export / share */}
      <div className="card-soft p-6 md:p-8 bg-gradient-to-br from-[var(--bg)] to-[var(--bg-2)]">
        <div className="flex items-start flex-wrap gap-5 justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-12 w-12 rounded-full bg-[color:var(--accent)]/15 flex items-center justify-center shrink-0">
              <BookOpen size={20} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            </div>
            <div className="min-w-0">
              <p className="overline">Personal Book</p>
              <h3 className="font-serif text-2xl text-[color:var(--ink)] mt-1">Your journey, held gently.</h3>
              <p className="text-sm text-[color:var(--ink-2)] mt-2 max-w-xl leading-relaxed">
                Write what your body is telling you. Upload pictures of where you are today.
                Whenever you want, export it as a personal book — for yourself, or to share with someone you trust.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadPdf} className="btn-primary !py-2 !px-4 !text-sm" data-testid="book-download">
              <Download size={14} strokeWidth={1.6} /> Download PDF
            </button>
            {share.enabled ? (
              <button onClick={revokeShare} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="book-share-revoke">
                <LinkIcon size={14} strokeWidth={1.6} /> Revoke link
              </button>
            ) : (
              <button onClick={createShare} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="book-share-create">
                <Share2 size={14} strokeWidth={1.6} /> Create share link
              </button>
            )}
          </div>
        </div>
        {share.enabled && share.share_url && (
          <div className="mt-5 rounded-xl border border-[color:var(--line-soft)] bg-[var(--bg)] p-3 flex items-center gap-2 flex-wrap" data-testid="book-share-url-row">
            <code className="text-xs text-[color:var(--ink-2)] break-all flex-1 min-w-0">{share.share_url}</code>
            <button onClick={copyShare} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="book-share-copy">
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        )}
      </div>

      {err && <p className="text-sm text-[color:var(--accent-2)]" data-testid="book-error">{err}</p>}

      {/* Pictures */}
      <section>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Camera size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            <h3 className="font-serif text-2xl text-[color:var(--ink)]">Pictures</h3>
            <span className="text-xs text-[color:var(--ink-3)]">· {pictures.length}</span>
          </div>
        </div>
        <div className="card-soft p-5" data-testid="picture-uploader">
          <input
            className="input-warm mb-3"
            placeholder="A caption for this picture (optional) — e.g. 'Morning light, quiet body'"
            value={picCaption}
            onChange={(e) => setPicCaption(e.target.value)}
            data-testid="picture-caption"
          />
          <FileUploader
            onUploaded={attachPicture}
            accept="image/*"
            label={picBusy ? "Adding…" : "Upload a picture"}
            testid="picture-upload"
          />
        </div>

        {pictures.length === 0 ? (
          <p className="text-[color:var(--ink-3)] italic mt-4 text-sm">No pictures yet. Any photo you upload will be remembered here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="picture-grid">
            {pictures.map((p) => (
              <figure key={p.id} className="card-soft p-3 group relative" data-testid={`picture-${p.id}`}>
                <img
                  src={pictureSrc(p)}
                  alt={p.caption || p.file_name}
                  className="w-full h-56 object-cover rounded-xl"
                  loading="lazy"
                />
                <figcaption className="mt-3 px-1 text-xs text-[color:var(--ink-2)]">
                  <p className="text-[color:var(--ink-3)] text-[11px] uppercase tracking-widest">{formatDate(p.taken_on)}</p>
                  {p.caption && <p className="mt-1 italic">{p.caption}</p>}
                </figcaption>
                <button onClick={() => removePicture(p.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--bg)]/90 rounded-full p-1.5 text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`picture-${p.id}-delete`}>
                  <Trash2 size={14} />
                </button>
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* Journal */}
      <section>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <NotebookPen size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            <h3 className="font-serif text-2xl text-[color:var(--ink)]">Journal</h3>
            <span className="text-xs text-[color:var(--ink-3)]">· {journal.length}</span>
          </div>
        </div>
        <form onSubmit={addEntry} className="card-soft p-5 space-y-3" data-testid="journal-form">
          <input
            className="input-warm"
            placeholder="A title for today (optional)"
            value={jTitle}
            onChange={(e) => setJTitle(e.target.value)}
            data-testid="journal-title"
          />
          <textarea
            required
            rows={5}
            className="input-warm"
            placeholder="What is your body asking for today? What's alive in you, what's tender, what softened?"
            value={jContent}
            onChange={(e) => setJContent(e.target.value)}
            data-testid="journal-content"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <input
              className="input-warm !py-2 !text-sm max-w-xs"
              placeholder="Mood (optional) — e.g. calm, anxious"
              value={jMood}
              onChange={(e) => setJMood(e.target.value)}
              data-testid="journal-mood"
            />
            <button disabled={jBusy || !jContent.trim()} className="btn-primary !py-2 !px-4 !text-sm ml-auto" data-testid="journal-submit">
              <Plus size={14} strokeWidth={1.6} /> {jBusy ? "Writing…" : "Add entry"}
            </button>
          </div>
        </form>

        {journal.length === 0 ? (
          <p className="text-[color:var(--ink-3)] italic mt-4 text-sm">Your journal is empty — write your first line whenever you're ready.</p>
        ) : (
          <div className="mt-5 space-y-4" data-testid="journal-list">
            {journal.map((j) => (
              <article key={j.id} className="card-soft p-5 group" data-testid={`journal-${j.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-3)]">
                      {formatDate(j.entry_date)}{j.mood ? ` · mood: ${j.mood}` : ""}
                    </p>
                    {j.title && <p className="font-serif text-xl text-[color:var(--ink)] mt-1">{j.title}</p>}
                  </div>
                  <button onClick={() => removeEntry(j.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]" data-testid={`journal-${j.id}-delete`}>
                    <Trash2 size={15} />
                  </button>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-[color:var(--ink-2)] leading-relaxed">{j.content}</p>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
